import { DatePicker } from '@components/DateTimePickers/DatePicker';
import { TimePicker } from '@components/DateTimePickers/TimePicker';
import { IntegerTimeInput } from '@components/IntegerTimeInput';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { orderStore } from '@store/orders';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import {
  timeIntegerToTimeString,
  timeStringToTimeInteger,
} from '@utils/dateFormat';
import { Accessor, Setter, Show, createEffect, on } from 'solid-js';

import {
  canEditDriverInTime,
  canEditDriverOutTime,
  dirtyTracking,
} from './StopHelpers';

export const stopWindowTimeFromStore = (
  isoDateTime: string | null | undefined,
): string => {
  if (!isoDateTime?.includes('T')) return '';
  return isoDateTime.split('T')[1]?.slice(0, 5) ?? '';
};

/** Sync local time from store; skip midnight when the picker is still empty (date-only edit). */
export const syncStopWindowTimeFromStore = (
  localTime: string,
  storeIso: string | null | undefined,
  setTime: Setter<string>,
): void => {
  const storeTime = stopWindowTimeFromStore(storeIso);
  if (!localTime && storeTime === '00:00') return;
  if (localTime !== storeTime) setTime(storeTime);
};

export type StopWindowSignals = {
  timeIn: [Accessor<string>, Setter<string>];
  timeOut: [Accessor<string>, Setter<string>];
  dateIn: [Accessor<string>, Setter<string>];
  dateOut: [Accessor<string>, Setter<string>];
};

type Props = {
  index: number;
  rowData: LastUsedTopStopModel;
  signals: StopWindowSignals;
  scratch: () =>
    | { driverInError?: string; driverOutError?: string }
    | undefined;
  onDriverTimeChange: (
    field: 'driverInTime' | 'driverOutTime',
    value: string | null,
    index: number,
    typedDate?: string,
    typedTime?: string,
  ) => void;
  datePickerClass?: string;
  testIdPrefix?: 'stop-date' | 'stop-time';
  hasCarrier: boolean;
};

export const StopWindowFields = (props: Props) => {
  const [timeIn, setTimeIn] = props.signals.timeIn;
  const [timeOut, setTimeOut] = props.signals.timeOut;
  const [dateIn, setDateIn] = props.signals.dateIn;
  const [dateOut, setDateOut] = props.signals.dateOut;

  const is24HourTimesEnabled = isFeatureFlagEnabled(
    FeatureFlags.enable_24_hour_in_and_out_times,
  );
  const carrierMissing = () =>
    isFeatureFlagEnabled(
      FeatureFlags.RequireCarrierAssignmentToProgressStatus,
    )() && !props.hasCarrier;

  //If rowdata changes (aka the main store),  then we need to update the local hooks
  createEffect(
    on(
      () => ({
        driverInTime: props.rowData.driverInTime ?? '',
        driverOutTime: props.rowData.driverOutTime ?? '',
      }),
      ({ driverInTime, driverOutTime }) => {
        const storeDateIn = driverInTime.split('T')[0] ?? '';
        const storeDateOut = driverOutTime.split('T')[0] ?? '';
        if (dateIn() !== storeDateIn) setDateIn(storeDateIn);
        syncStopWindowTimeFromStore(timeIn(), driverInTime, setTimeIn);
        if (dateOut() !== storeDateOut) setDateOut(storeDateOut);
        syncStopWindowTimeFromStore(timeOut(), driverOutTime, setTimeOut);
      },
    ),
  );

  const applyIn = () => {
    const d = dateIn();
    const t = timeIn();
    props.onDriverTimeChange(
      'driverInTime',
      d ? `${d}T${t || '00:00'}` : null,
      props.index,
      d,
      t,
    );
  };

  const applyOut = () => {
    const d = dateOut();
    const t = timeOut();
    props.onDriverTimeChange(
      'driverOutTime',
      d ? `${d}T${t || '00:00'}` : null,
      props.index,
      d,
      t,
    );
  };

  const testIdPrefix = () => props.testIdPrefix ?? 'stop-date';

  return (
    <>
      <div class="text-sm text-[rgba(0,0,0,0.87)]">Delivery Window</div>
      <div class="text-sm text-gray-500">Driver In</div>
      <div class="flex gap-2 mt-2">
        <DatePicker
          name="driverInTime"
          label="Date"
          class={`flex-3 max-w-[140px] ${props.datePickerClass ?? ''}`}
          value={dateIn()}
          handleChange={(value: string) => {
            dirtyTracking(
              props.rowData.pickUp ? 'pickUp' : 'dropOff',
              'in',
              props.index,
            );
            setDateIn(value);
            applyIn();
          }}
          disabled={
            orderStore.isReadOnly ||
            carrierMissing() ||
            !canEditDriverInTime(props.index)
          }
          dataTestId={`${testIdPrefix()}-${props.index}-driverInDate`}
        />
        <Show
          when={is24HourTimesEnabled()}
          fallback={
            <TimePicker
              name="driverInTime"
              label="Time"
              value={timeIn()}
              handleChange={(value: string) => {
                dirtyTracking(
                  props.rowData.pickUp ? 'pickUp' : 'dropOff',
                  'in',
                  props.index,
                );
                setTimeIn(value);
                applyIn();
              }}
              disabled={
                orderStore.isReadOnly ||
                carrierMissing() ||
                !canEditDriverInTime(props.index)
              }
              dataTestId={`${testIdPrefix()}-${props.index}-driverInTime`}
            />
          }
        >
          <IntegerTimeInput
            label="Time"
            placeholder="HH:MM"
            allowManualEntry
            allowMidnight
            floatLabel
            variant="form"
            height="h-[30px]"
            class="w-[90px]"
            value={timeStringToTimeInteger(timeIn())}
            onChange={(value) => {
              dirtyTracking(
                props.rowData.pickUp ? 'pickUp' : 'dropOff',
                'in',
                props.index,
              );
              setTimeIn(timeIntegerToTimeString(value));
              applyIn();
            }}
            disabled={
              orderStore.isReadOnly ||
              carrierMissing() ||
              !canEditDriverInTime(props.index)
            }
            dataTestId={`${testIdPrefix()}-${props.index}-driverInTime`}
          />
        </Show>
      </div>
      <Show when={props.scratch()?.driverInError}>
        <div
          class="text-xs text-red-600 mt-1"
          data-testid={`${testIdPrefix()}-${props.index}-driverInError`}
        >
          {props.scratch()?.driverInError}
        </div>
      </Show>
      <div class="text-sm text-gray-500">Driver Out</div>
      <div class="flex gap-2 mt-2">
        <DatePicker
          name="driverOutTime"
          label="Date"
          class={`flex-3 max-w-[140px] ${props.datePickerClass ?? ''}`}
          value={dateOut()}
          handleChange={(value: string) => {
            dirtyTracking(
              props.rowData.pickUp ? 'pickUp' : 'dropOff',
              'out',
              props.index,
            );
            setDateOut(value);
            applyOut();
          }}
          disabled={
            orderStore.isReadOnly ||
            carrierMissing() ||
            !canEditDriverOutTime(props.index)
          }
          dataTestId={`${testIdPrefix()}-${props.index}-driverOutDate`}
        />
        <Show
          when={is24HourTimesEnabled()}
          fallback={
            <TimePicker
              name="driverOutTime"
              label="Time"
              value={timeOut()}
              handleChange={(value: string) => {
                dirtyTracking(
                  props.rowData.pickUp ? 'pickUp' : 'dropOff',
                  'out',
                  props.index,
                );
                setTimeOut(value);
                applyOut();
              }}
              disabled={
                orderStore.isReadOnly ||
                carrierMissing() ||
                !canEditDriverOutTime(props.index)
              }
              dataTestId={`${testIdPrefix()}-${props.index}-driverOutTime`}
            />
          }
        >
          <IntegerTimeInput
            label="Time"
            placeholder="HH:MM"
            allowManualEntry
            allowMidnight
            floatLabel
            variant="form"
            height="h-[30px]"
            class="w-[90px]"
            value={timeStringToTimeInteger(timeOut())}
            onChange={(value) => {
              dirtyTracking(
                props.rowData.pickUp ? 'pickUp' : 'dropOff',
                'out',
                props.index,
              );
              setTimeOut(timeIntegerToTimeString(value));
              applyOut();
            }}
            disabled={
              orderStore.isReadOnly ||
              carrierMissing() ||
              !canEditDriverOutTime(props.index)
            }
            dataTestId={`${testIdPrefix()}-${props.index}-driverOutTime`}
          />
        </Show>
      </div>
      <Show when={props.scratch()?.driverOutError}>
        <div
          class="text-xs text-red-600 mt-1"
          data-testid={`${testIdPrefix()}-${props.index}-driverOutError`}
        >
          {props.scratch()?.driverOutError}
        </div>
      </Show>
      <div class="text-xs text-armstrong-blue h-[40px]">
        Time should be entered in the time zone of the stop location.
      </div>
    </>
  );
};
